import type { ConfigChange } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { getConfigDiff, listConfigVersions, revertConfig } from '../api/config.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { EmptyState } from './EmptyState.js';
import { formatInstant } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Version history, comparison and revert (knowledge doc section 11's React
 * features).
 *
 * The revert is the part worth reading carefully. It does **not** write an old
 * document back — a version is the result of merging three layers and cannot be
 * turned back into them, so that would hold only until the next edit to any layer
 * quietly undid it. It restores the layers that produced the version, which is
 * wider than one server and which the dialog says before anybody clicks.
 */

export function ConfigHistoryCard({
  serverId,
  timezone,
}: {
  serverId: string;
  timezone: string;
}): ReactNode {
  const queryClient = useQueryClient();
  const [comparing, setComparing] = useState<number | null>(null);
  const [reverting, setReverting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reverted, setReverted] = useState<number | null>(null);

  const versions = useQuery({
    queryKey: ['config-versions', serverId],
    queryFn: () => listConfigVersions(serverId),
  });

  const revert = useMutation({
    mutationFn: (version: number) => revertConfig(serverId, version),
    onSuccess: (_response, version) => {
      setReverting(null);
      setError(null);
      setReverted(version);
      void queryClient.invalidateQueries({ queryKey: ['config-versions', serverId] });
      void queryClient.invalidateQueries({ queryKey: ['config-layer'] });
      void queryClient.invalidateQueries({ queryKey: ['config-rollout'] });
      void queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'Could not revert to that version.');
    },
  });

  if (versions.isPending) {
    return (
      <Card title="Settings history">
        <Loading />
      </Card>
    );
  }

  if (versions.isError) {
    return (
      <Card title="Settings history">
        <Alert tone="danger" title="Could not load the history">
          Try again in a moment.
        </Alert>
      </Card>
    );
  }

  const rows = versions.data;

  return (
    <Card
      title="Settings history"
      description="Every version this server has been given. Reverting restores the settings that produced one."
    >
      {rows.length === 0 && (
        <EmptyState title="No versions yet">
          One is built the first time this server asks for its settings.
        </EmptyState>
      )}

      {reverted !== null && (
        <Alert tone="success" title={`Reverted to version ${String(reverted)}`}>
          The settings that produced it are back, and a new version carries them.
        </Alert>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">Config versions for this server</caption>
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Written</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const previous = rows[index + 1]?.version;
                return (
                  <tr key={row.version}>
                    <th scope="row">
                      {row.version}
                      {index === 0 && <span className="badge badge-success">newest</span>}
                    </th>
                    <td className="text-sm">{formatInstant(row.createdAt, { timezone })}</td>
                    <td>
                      <div className="row">
                        {previous !== undefined && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setComparing(comparing === row.version ? null : row.version);
                            }}
                          >
                            {comparing === row.version ? 'Hide changes' : 'What changed'}
                          </Button>
                        )}
                        {index !== 0 && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setError(null);
                              setReverting(row.version);
                            }}
                          >
                            Revert to this
                          </Button>
                        )}
                      </div>
                      {comparing === row.version && previous !== undefined && (
                        <DiffList serverId={serverId} from={previous} to={row.version} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* A failure stays inside the dialog rather than appearing in the card as
          well: the action is still open, and the same sentence in two places reads
          as two things having gone wrong. */}
      {reverting !== null && (
        <ConfirmDialog
          title={`Revert to version ${String(reverting)}?`}
          description="This puts back the global, group and server settings as they were then — so it reaches every server those layers apply to, not only this one. A new version carries them; nothing in the history is removed."
          confirmLabel="Revert the settings"
          busy={revert.isPending}
          error={error}
          onConfirm={() => {
            revert.mutate(reverting);
          }}
          onCancel={() => {
            setReverting(null);
          }}
        />
      )}
    </Card>
  );
}

function DiffList({
  serverId,
  from,
  to,
}: {
  serverId: string;
  from: number;
  to: number;
}): ReactNode {
  const diff = useQuery({
    queryKey: ['config-diff', serverId, from, to],
    queryFn: () => getConfigDiff(serverId, from, to),
  });

  if (diff.isPending) return <p className="muted text-sm">Comparing…</p>;
  if (diff.isError) return <p className="muted text-sm">Could not compare those versions.</p>;
  if (diff.data.changes.length === 0) {
    // Possible after a revert: two versions with the same settings under different
    // numbers. Saying so beats an empty box.
    return <p className="muted text-sm">Nothing changed between these two versions.</p>;
  }

  return (
    <dl className="details">
      {diff.data.changes.map((change) => (
        <ChangeRow key={change.path} change={change} />
      ))}
    </dl>
  );
}

function ChangeRow({ change }: { change: ConfigChange }): ReactNode {
  return (
    <>
      <dt>{change.path}</dt>
      <dd className="text-sm">
        {change.kind === 'added' ? (
          <>set to {describe(change.after)}</>
        ) : change.kind === 'removed' ? (
          <>no longer set (was {describe(change.before)})</>
        ) : (
          <>
            {describe(change.before)} → {describe(change.after)}
          </>
        )}
      </dd>
    </>
  );
}

const describe = (value: unknown): string => {
  if (value === undefined || value === null) return 'nothing';
  if (Array.isArray(value)) return value.length === 0 ? 'nothing' : value.join(', ');
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (typeof value === 'object') return JSON.stringify(value);
  // Narrowed above to what has a useful string form: an object would otherwise
  // read as `[object Object]` in a diff, which tells nobody anything.
  return typeof value === 'string' ? value : JSON.stringify(value);
};
